import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Settings, defaultSettings } from '../types/settings';

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Settings) => void;
  updateSetting: <K extends keyof Settings, SK extends keyof Settings[K]>(
    category: K,
    key: SK,
    value: Settings[K][SK]
  ) => void;
  isLoading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load settings from sessionStorage
    const loadSettings = () => {
      const savedSettings = sessionStorage.getItem('settings');
      if (savedSettings) {
        try {
          setSettings(JSON.parse(savedSettings));
        } catch (e) {
          console.error('Error parsing saved settings', e);
          setSettings(defaultSettings);
        }
      }
      setIsLoading(false);
    };

    loadSettings();
  }, []);

  // Save settings to sessionStorage whenever they change
  useEffect(() => {
    if (!isLoading) {
      sessionStorage.setItem('settings', JSON.stringify(settings));
    }
  }, [settings, isLoading]);

  const updateSettings = (newSettings: Settings) => {
    setSettings(newSettings);
  };

  const updateSetting = <K extends keyof Settings, SK extends keyof Settings[K]>(
    category: K,
    key: SK,
    value: Settings[K][SK]
  ) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [category]: {
        ...prevSettings[category],
        [key]: value
      }
    }));
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, updateSetting, isLoading }}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
